import { createFileRoute, Link } from "@tanstack/react-router";
import { ServiceDetail } from "@/components/ServiceDetail";
import { faqPageSchema, type Faq } from "@/data/serviceFaqs";
import __apexTheater from "@/assets/Apex_Theater.webp.asset.json";
const hero = __apexTheater.url;
import apexTheaterRichImg from "@/assets/Apex_Theater.webp.asset.json";

const URL = "https://apexav.com/home-theater-near-me";

const NEARBY = [
  { name: "Cherry Creek", to: "/cherry-creek/home-theater-installation" as const },
  { name: "Highlands Ranch", to: "/highlands-ranch/home-theater-installation" as const },
  { name: "Castle Rock", to: "/castle-rock/home-theater-installation" as const },
  { name: "Castle Pines", to: "/castle-pines/home-theater-installation" as const },
];

const htnFaqs: Faq[] = [
          {
            q: "Who installs home theaters near me in Denver?",
            a: "Apex AV is a Denver-headquartered home theater design and installation company. We're the local installer for Cherry Creek, Hilltop, Polo Club, Greenwood Village, Cherry Hills Village, Highlands Ranch, Castle Pines, Castle Rock, Lone Tree, Parker, and the surrounding Front Range. Our team lives here — we're not a franchise or national chain flying crews in.",
          },
          {
            q: "How quickly can you visit my home?",
            a: "Most Denver Metro consultations are scheduled within 3–5 business days of the first call. The initial visit is free and usually takes 60–90 minutes — we measure the room, review your existing gear, and leave with enough information to send a real design and price back within a week.",
          },
          {
            q: "How much does a home theater cost in Denver?",
            a: "Media rooms with a large TV, 5.1.2 or 7.1.4 in-wall Atmos, and one-button lighting scenes typically run $25,000–$60,000 installed in Denver. Dedicated theater rooms with 4K/8K laser projection, motorized screen, tiered seating, acoustic treatment, and full Dolby Atmos start around $60,000 and range up to $300,000+ for Cherry Creek and Castle Pines reference builds.",
          },
          {
            q: "Can I see a finished theater near me before signing?",
            a: "Yes. Once we scope your project, we can typically arrange a walkthrough of a completed Denver-area theater with similar size and budget. Nothing sells a Dolby Atmos build like sitting in one.",
          },
          {
            q: "Do you serve the whole Front Range or just central Denver?",
            a: "We install across Denver Metro and the greater Front Range — including Boulder, Evergreen, Golden, Arvada, Westminster, Broomfield, Thornton, Aurora, Centennial, Littleton, and Colorado Springs on larger projects.",
          },
];

export const Route = createFileRoute("/home-theater-near-me")({
  head: () => ({
    meta: [
      { title: "Home Theater Near Me | Denver Home Theater Installation — Apex AV" },
      { name: "description", content: "Searching \"home theater near me\" in Denver? Apex AV designs and installs reference-grade home theaters across the Denver Metro. Free in-home consultation." },
      { name: "keywords", content: "home theater near me, home theater installers near me, home theater installation near me, home theater company near me, home theater design near me, Denver home theater near me" },
      { property: "og:title", content: "Home Theater Near Me — Apex AV Denver" },
      { property: "og:description", content: "Local Denver home theater installers. Dedicated theaters, media rooms, and Dolby Atmos design." },
      { property: "og:image", content: `https://apexav.com${apexTheaterRichImg.url}` },
      { property: "og:image:alt", content: "Apex AV custom home theater installation in Denver, Colorado" },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Home Theater Near Me — Apex AV Denver" },
      { name: "twitter:description", content: "Denver's local home theater installer." },
      { name: "twitter:image", content: `https://apexav.com${apexTheaterRichImg.url}` },
    ],
    links: [
      { rel: "canonical", href: URL },
      { rel: "preload", as: "image", href: hero, fetchPriority: "high" } as never,
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            faqPageSchema(htnFaqs, URL),
            {
              "@type": ["LocalBusiness", "HomeAndConstructionBusiness"],
              "@id": `${URL}#localbusiness`,
              name: "Apex AV — Home Theater Installation Near Denver",
              image: `https://apexav.com${apexTheaterRichImg.url}`,
              logo: "https://apexav.com/logo.png",
              url: URL,
              telephone: "+1-303-317-3233",
              email: "info@apexav.com",
              priceRange: "$$$",
              address: {
                "@type": "PostalAddress",
                streetAddress: "2921 W 38th AVE STE 169",
                addressLocality: "Denver",
                addressRegion: "CO",
                postalCode: "80211",
                addressCountry: "US",
              },
              geo: { "@type": "GeoCoordinates", latitude: 39.7714, longitude: -105.0234 },
              areaServed: [
                { "@type": "City", name: "Denver, CO" },
                { "@type": "AdministrativeArea", name: "Denver Metro & Front Range, Colorado" },
              ],
              openingHoursSpecification: [
                { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], opens: "08:00", closes: "18:00" },
                { "@type": "OpeningHoursSpecification", dayOfWeek: "Saturday", opens: "09:00", closes: "16:00" },
              ],
              sameAs: [
                "https://share.google/jMyCOceBfsaHfeVQp",
                "https://x.com/ApexAV80211",
                "https://www.facebook.com/apexavdenver",
              ],
              parentOrganization: { "@id": "https://apexav.com/#organization" },
              makesOffer: {
                "@type": "Offer",
                priceCurrency: "USD",
                price: 25000,
                itemOffered: { "@type": "Service", name: "Home Theater Installation Near Denver" },
              },
            },
            {
              "@type": "Service",
              name: "Home Theater Installation Near Denver",
              description: "Local Denver home theater design and installation.",
              url: URL,
              provider: { "@id": "https://apexav.com/#organization" },
              areaServed: { "@type": "AdministrativeArea", name: "Denver Metro & Front Range, Colorado" },
              serviceType: "Home Theater Installation",
              offers: { "@type": "Offer", priceCurrency: "USD", price: "25000" },
            },
          ],
        }),
      },
    ],
  }),
  component: HomeTheaterNearMe,
});

function HomeTheaterNearMe() {
  return (
    <>
      <ServiceDetail
        eyebrow="Home theater near me"
        title="Local Denver home theater installers — near you."
        intro={<>Searching <strong>"home theater near me"</strong>? Apex AV is Denver's local home theater design & install company, serving Cherry Creek, Hilltop, Polo Club, Greenwood Village, Cherry Hills Village, Highlands Ranch, Castle Pines, Castle Rock, Lone Tree, Parker, Littleton, Centennial, and the entire Front Range. Free in-home consultation, fixed-bid pricing, and a lifetime local relationship — not a national chain.</>}
        image={hero}
        imageAlt="Local home theater installer near me in Denver, Colorado — Apex AV Dolby Atmos theater build"
        video="/videos/home-theater-video.mp4"
        startingPrice="$25,000"
        priceNote="Media rooms from $25K. Dedicated Dolby Atmos theaters $60K–$300K+."
        bullets={[
          "Local Denver installers — not a national chain",
          "Free in-home theater design consultation",
          "Fixed-scope, fixed-bid pricing before install",
          "Dedicated theaters, media rooms, and multi-purpose builds",
          "Dolby Atmos 7.4.4, 9.4.6, and reference-grade calibration",
          "4K & 8K laser projection (JVC, Sony, Barco)",
          "Serving Denver Metro & Front Range same-week",
          "10-year lifetime local support relationship",
        ]}
        features={[
          { title: "Same-week visits", desc: "In-home consultations across Denver Metro, usually within 5 business days." },
          { title: "Local calibration", desc: "Denver-based team returns for post-install tuning as your room settles." },
          { title: "Actual portfolio", desc: "Ask to walk through a finished theater near you — we schedule client tours." },
        ]}
        faqs={htnFaqs}
      />
      <section className="container mx-auto px-6 py-16 border-t border-border">
        <h2 className="font-display text-2xl md:text-3xl font-bold">
          Neighborhood-specific home theater pages
        </h2>
        <p className="mt-2 text-muted-foreground max-w-2xl">
          Every Denver neighborhood has its own architecture, HOA rules, and construction quirks. Pick yours for a page tuned to it.
        </p>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {NEARBY.map((n) => (
            <Link
              key={n.name}
              to={n.to}
              className="rounded-xl border border-border bg-card p-5 shadow-card hover:border-brand/50 transition"
            >
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Home theater</div>
              <div className="mt-2 font-display text-lg font-semibold">{n.name}</div>
              <div className="mt-3 text-sm text-brand-text">View page →</div>
            </Link>
          ))}
        </div>
      </section>

      <section className="container mx-auto px-6 pb-20">
        <h2 className="font-display text-2xl md:text-3xl font-bold">Planning your room? Start with the guide</h2>
        <p className="mt-3 max-w-2xl text-muted-foreground">
          Our{" "}
          <Link
            to="/blog/custom-home-theater-design-guide"
            className="font-semibold text-brand-text underline underline-offset-4"
          >
            custom home theater design guide
          </Link>{" "}
          covers room dimensions, screen and seating geometry, immersive speaker layout, and acoustic treatment for
          Denver-area homes.
        </p>
        <Link
          to="/blog/custom-home-theater-design-guide"
          className="mt-6 inline-flex items-center gap-2 rounded-md border border-border bg-card px-5 py-3 text-sm font-semibold hover:border-brand/50 transition"
        >
          Read the custom home theater design guide →
        </Link>
      </section>
    </>
  );
}
